import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

export type AlertType = 'info' | 'warning' | 'error' | 'success';

export interface AlertState {
  isOpen: boolean;
  type: AlertType;
  message: string;
  title?: string;
  isConfirm: boolean;
  onConfirm?: () => void | Promise<void>;
  onCancel?: () => void;
  confirmText?: string;
  cancelText?: string;
}

interface AlertContextType {
  showAlert: (message: string, type?: AlertType, title?: string) => Promise<void>;
  showConfirm: (
    message: string,
    onConfirm?: () => void | Promise<void>,
    onCancel?: () => void,
    type?: AlertType,
    title?: string,
    confirmText?: string,
    cancelText?: string
  ) => Promise<boolean>;
  alertState: AlertState;
  closeAlert: () => void;
}

const AlertContext = createContext<AlertContextType | undefined>(undefined);

export const useAlert = () => {
  const context = useContext(AlertContext);
  if (!context) {
    throw new Error('useAlert must be used within an AlertProvider');
  }
  return context;
};

interface AlertProviderProps {
  children: ReactNode;
}

export const AlertProvider: React.FC<AlertProviderProps> = ({ children }) => {
  const [alertState, setAlertState] = useState<AlertState>({
    isOpen: false,
    type: 'info',
    message: '',
    isConfirm: false,
  });

  const closeAlert = useCallback(() => {
    setAlertState((prev) => ({ ...prev, isOpen: false }));
  }, []);

  const showAlert = useCallback(
    (message: string, type: AlertType = 'info', title?: string): Promise<void> => {
      return new Promise((resolve) => {
        setAlertState({
          isOpen: true,
          type,
          message,
          title,
          isConfirm: false,
          onConfirm: () => {
            closeAlert();
            resolve();
          },
        });
      });
    },
    [closeAlert]
  );

  const showConfirm = useCallback(
    (
      message: string,
      onConfirm?: () => void | Promise<void>,
      onCancel?: () => void,
      type: AlertType = 'warning',
      title?: string,
      confirmText: string = 'Confirm',
      cancelText: string = 'Cancel'
    ): Promise<boolean> => {
      return new Promise((resolve) => {
        setAlertState({
          isOpen: true,
          type,
          message,
          title,
          isConfirm: true,
          confirmText,
          cancelText,
          onConfirm: async () => {
            if (onConfirm) {
              await onConfirm();
            }
            closeAlert();
            resolve(true);
          },
          onCancel: () => {
            if (onCancel) {
              onCancel();
            }
            closeAlert();
            resolve(false);
          },
        });
      });
    },
    [closeAlert]
  );

  return (
    <AlertContext.Provider
      value={{
        showAlert,
        showConfirm,
        alertState,
        closeAlert,
      }}
    >
      {children}
    </AlertContext.Provider>
  );
};

